import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

// Shared building blocks for the per-role home screens, so they stay visually consistent.
// The heading is NOT here: the shell (SectionTabs) prints the role's label and question once.

/** Placeholder with the shape every home screen shares; shown while the role or its data is loading. */
export const HomeSkeleton = () => (
  <div className="space-y-6" aria-busy="true">
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}</div>
    <div className="grid gap-6 lg:grid-cols-3"><Skeleton className="h-72 lg:col-span-2" /><Skeleton className="h-72" /></div>
  </div>
);

export const HomeActions = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-wrap justify-end gap-2">{children}</div>
);

interface StatTileProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon: ReactNode;
  /** Draws attention when the number is a problem; colour is never the only signal (the hint says why). */
  tone?: 'default' | 'warning' | 'critical';
  onClick?: () => void;
}

export const StatTile = ({ label, value, hint, icon, tone = 'default', onClick }: StatTileProps) => {
  const body = (
    <CardContent className="p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{label}</p>
        <span className={cn(tone === 'critical' ? 'text-destructive' : tone === 'warning' ? 'text-warning' : 'text-muted-foreground')} aria-hidden="true">{icon}</span>
      </div>
      <p className="mt-2 text-3xl font-bold tabular-nums text-foreground">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </CardContent>
  );
  return (
    <Card className={cn(onClick && 'transition-colors hover:bg-muted/30')}>
      {onClick ? (
        <button type="button" onClick={onClick} className="block w-full rounded-lg text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {body}
        </button>
      ) : body}
    </Card>
  );
};

export const StatRow = ({ children }: { children: ReactNode }) => (
  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{children}</div>
);

interface HomePanelProps {
  title: string;
  hint?: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}

export const HomePanel = ({ title, hint, action, className, children }: HomePanelProps) => (
  <Card className={className}>
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </div>
        {action}
      </div>
    </CardHeader>
    <CardContent className="space-y-2">{children}</CardContent>
  </Card>
);

export const HomeEmpty = ({ children }: { children: ReactNode }) => (
  <p className="py-6 text-center text-sm text-muted-foreground">{children}</p>
);

/** A full-width clickable row with a visible focus ring and a 44px minimum touch height. */
export const HomeRow = ({ onClick, label, children }: { onClick: () => void; label?: string; children: ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="flex min-h-11 w-full items-center gap-3 rounded-lg border border-border p-3 text-start transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  >
    {children}
  </button>
);
